import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";
import LandingPage from "@/pages/LandingPage";

// Every route except the homepage is lazy so first paint doesn't pay for the
// whole site. Keep LandingPage eager — it's the most common entry.
const AIVideoProductionPage = lazy(() => import("@/pages/AIVideoProductionPage"));
const PerformanceCreativePage = lazy(() => import("@/pages/PerformanceCreativePage"));
const AIViralContentPage = lazy(() => import("@/pages/AIViralContentPage"));
const AIAvatarVideosPage = lazy(() => import("@/pages/AIAvatarVideosPage"));
const AIUGCVideosPage = lazy(() => import("@/pages/AIUGCVideosPage"));
const MicroDramaPage = lazy(() => import("@/pages/MicroDramaPage"));
const PlatformsPage = lazy(() => import("@/pages/solutions/PlatformsPage"));
const EducationPage = lazy(() => import("@/pages/solutions/EducationPage"));
const GrowthPage = lazy(() => import("@/pages/solutions/GrowthPage"));
const PocketFMPage = lazy(() => import("@/pages/case-studies/PocketFMPage"));
const MazdaPage = lazy(() => import("@/pages/case-studies/MazdaPage"));
const DashversePage = lazy(() => import("@/pages/case-studies/DashversePage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

const RouteFallback = () => <div className="min-h-screen bg-ground" aria-busy="true" />;

function App() {
  return (
    <div className="App min-h-screen bg-ground">
      <MotionConfig reducedMotion="user">
        <BrowserRouter>
          <ScrollToTop />
          <ErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/ai-video-production" element={<AIVideoProductionPage />} />
                <Route path="/performance-creative" element={<PerformanceCreativePage />} />
                <Route path="/ai-viral-content" element={<AIViralContentPage />} />
                <Route path="/ai-avatar-videos" element={<AIAvatarVideosPage />} />
                <Route path="/ai-ugc-videos" element={<AIUGCVideosPage />} />
                <Route path="/micro-drama-production" element={<MicroDramaPage />} />
                <Route path="/solutions/platforms" element={<PlatformsPage />} />
                <Route path="/solutions/education" element={<EducationPage />} />
                <Route path="/solutions/growth" element={<GrowthPage />} />
                <Route path="/case-studies/pocket-fm" element={<PocketFMPage />} />
                <Route path="/case-studies/mazda" element={<MazdaPage />} />
                <Route path="/case-studies/dashverse" element={<DashversePage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </MotionConfig>
      {/* Reads from the token layer rather than carrying its own colours. The
          previous values were survivors of the acid-green system this palette
          replaced two designs ago — a green border nothing else on the site
          could still produce. */}
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'var(--surface)',
            color: 'var(--bone)',
            border: '1px solid var(--line)',
            borderRadius: 0,
          },
        }}
      />
    </div>
  );
}

export default App;
